import React, { ReactElement, useState, useEffect } from 'react';
import { Modal, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import Lottie from 'react-lottie';
import NA_Automation_Loader from '../../../../assets/images/lottie-json/NA_Automation_loader.json';
import './AutomationLoaderModal.less';

const AutomationLoaderModal = (props: any): ReactElement => {
  const { isClaimDetailPage, onCancel, isHISLoading } = props;
  const { isAutomatedNewAdmission } = useSelector((state: RootState): any => state.hospital);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  // Move loadingTexts outside to avoid recreation on every render
  const loadingTexts: string[] = React.useMemo((): string[] => {
    const loadingMessages = [
      'Fetching Patient Details...',
      'Fetching Bill Details...',
      'Fetching Documents...',
      'Beneficiary Search in Progress...',
    ];

    return isClaimDetailPage
      ? ['Fetching Claim Details...', ...loadingMessages.slice(0, 3)]
      : loadingMessages;
  }, [isClaimDetailPage]);

  const NA_automation_loader =  {
    loop: true,
    autoplay: true,
    animationData: NA_Automation_Loader,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };
  useEffect((): (() => void) | void => {
    if (isAutomatedNewAdmission) {
      // Reset states
      setShowCancelButton(false);
      setTimerExpired(false);

      const interval = setInterval((): void => {
        setCurrentTextIndex((prevIndex: number): number => (prevIndex + 1) % loadingTexts.length);
      }, 2000); // Change text every 2 seconds

      // Set timer expired after 5 seconds
      const cancelButtonTimer = setTimeout((): void => {
        setTimerExpired(true);
        // if HIS data is fetched, show cancel button immediately after timer expires
        if (!isHISLoading) {
          setShowCancelButton(true);
        }
      }, 8000);

      return (): void => {
        clearInterval(interval);
        clearTimeout(cancelButtonTimer);
      };
    } else {
      // Reset states when modal closes
      setShowCancelButton(false);
      setTimerExpired(false);
    }
  }, [isAutomatedNewAdmission, loadingTexts.length]);

  // Effect to show cancel button when HIS data is fetched after timer expires
  useEffect((): void => {
    if (timerExpired && !isHISLoading && !showCancelButton) {
      setShowCancelButton(true);
    }
  }, [isHISLoading, timerExpired, showCancelButton]);

  const maskStyleConfig: Record<string, string> = {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(2px)',
  };

  return (
    <Modal
      visible={isAutomatedNewAdmission}
      footer={null}
      closable={false}
      centered={true}
      maskClosable={false}
      className="automation-loader-modal"
      width={320}
      maskStyle={maskStyleConfig}
    >
      <div className="automation-loader-content">
        <div className="automation-loader-text">
          {loadingTexts[currentTextIndex]}
        </div>
        <Lottie options={NA_automation_loader}/>
        {onCancel && showCancelButton && (
          <div className="cancel-automation-container">
            <Button
              type="default"
              shape="circle"
              size="small"
              onClick={onCancel}
              className="cancel-automation-btn"
              icon={<CloseOutlined />}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AutomationLoaderModal;
